import React, { useState } from 'react';
import { Plus, Search, Building, Trash2, Edit2, FileText, CheckCircle2, AlertCircle } from 'lucide-react';
import { Card, Button, Input, Badge, Select } from '../../components/ui/index.jsx';
import { Modal } from '../../components/ui/Modal';
import { useGet } from '../../hooks/useApi';
import api from '../../utils/api';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';

const PartnerList = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [isPartnerModalOpen, setIsPartnerModalOpen] = useState(false);
    const [isContractModalOpen, setIsContractModalOpen] = useState(false);
    const [editingPartner, setEditingPartner] = useState(null);
    const [selectedPartner, setSelectedPartner] = useState(null);
    
    const { success, error: toastError } = useToast();
    const { hasPermission } = useAuth();
    const canCreate = hasPermission('partners', 'create');

    const { data: partnersResponse, loading, refresh } = useGet(`/partners`);
    const partners = partnersResponse || [];

    const filteredPartners = partners.filter(p => p.name.toLowerCase().includes(searchTerm.toLowerCase()));

    const currentPartner = selectedPartner ? (partners.find(p => p.id === selectedPartner.id) || selectedPartner) : null;

    const handlePartnerSubmit = async (formData) => {
        try {
            if (editingPartner) {
                await api.put(`/partners/${editingPartner.id}`, formData);
                success('تم تحديث الشريك بنجاح');
            } else {
                await api.post('/partners', formData);
                success('تم إضافة الشريك بنجاح');
            }
            setIsPartnerModalOpen(false);
            setEditingPartner(null);
            refresh();
        } catch (err) {
            toastError(err.response?.data?.message || 'خطأ أثناء الحفظ');
        }
    };

    const handleContractSubmit = async (formData) => {
        try {
            await api.post(`/partners/${selectedPartner.id}/contracts`, formData);
            success('تم إضافة العقد بنجاح');
            setIsContractModalOpen(false);
            refresh();
        } catch (err) {
            toastError(err.response?.data?.message || 'خطأ أثناء الحفظ');
        }
    };

    const handleUpdateContractStatus = async (partnerId, contractId, newStatus) => {
        try {
            await api.put(`/partners/${partnerId}/contracts/${contractId}/status`, { status: newStatus });
            success('تم تحديث حالة العقد');
            refresh();
        } catch (err) {
            toastError('خطأ أثناء التحديث');
        }
    };

    const handleDeleteContract = async (partnerId, contractId) => {
        if (window.confirm('هل أنت متأكد من حذف هذا العقد؟')) {
            try {
                await api.delete(`/partners/${partnerId}/contracts/${contractId}`);
                success('تم حذف العقد بنجاح');
                refresh();
            } catch (err) {
                toastError('خطأ أثناء الحذف');
            }
        }
    };

    const handleDeletePartner = async (id) => {
        if (window.confirm('هل أنت متأكد من حذف هذا الشريك؟ سيتم حذف جميع إيداعاته وعقوده.')) {
            try {
                await api.delete(`/partners/${id}`);
                success('تم حذف الشريك بنجاح');
                refresh();
            } catch (err) {
                toastError('خطأ أثناء الحذف');
            }
        }
    };

    return (
        <div className="space-y-6 text-right" dir="rtl">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h2 className="text-xl text-slate-800 flex items-center gap-2">
                        <Building className="w-6 h-6 text-primary" />
                        إدارة الشركاء
                    </h2>
                    <p className="text-slate-400 text-[11px] mt-1">إضافة الشركاء وإدارة عقودهم</p>
                </div>
                <div className="flex items-center gap-3">
                    {canCreate && (
                        <Button onClick={() => { setEditingPartner(null); setIsPartnerModalOpen(true); }} className="shadow-lg shadow-primary/20">
                            <Plus className="w-4 h-4 ml-2" />
                            إضافة شريك جديد
                        </Button>
                    )}
                </div>
            </div>

            <Card className="p-4 bg-white/50 border-slate-100">
                <Input 
                    placeholder="بحث عن شريك..." 
                    icon={Search} 
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </Card>

            <Card className="overflow-hidden border-slate-100">
                <div className="overflow-x-auto">
                    <table className="w-full text-right font-arabic">
                        <thead>
                            <tr className="bg-slate-50 border-b border-slate-100">
                                <th className="px-6 py-4 text-[11px] text-slate-400 uppercase">اسم الشريك</th>
                                <th className="px-6 py-4 text-[11px] text-slate-400 uppercase">العقود النشطة</th>
                                <th className="px-6 py-4 text-[11px] text-slate-400 uppercase">جميع العقود</th>
                                <th className="px-6 py-4 text-[11px] text-slate-400 uppercase text-center w-40">الإجراءات</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {loading ? (
                                <tr><td colSpan="4" className="py-12 text-center animate-pulse">جاري التحميل...</td></tr>
                            ) : filteredPartners.length === 0 ? (
                                <tr><td colSpan="4" className="py-12 text-center text-slate-400">لا يوجد شركاء</td></tr>
                            ) : (
                                filteredPartners.map(partner => (
                                    <tr key={partner.id} className="hover:bg-slate-50/50 transition-colors">
                                        <td className="px-6 py-4 text-sm font-bold text-slate-800">{partner.name}</td>
                                        <td className="px-6 py-4">
                                            {partner.contracts?.filter(c => c.status === 'active').map(c => (
                                                <Badge key={c.id} variant="success" className="ml-1 mb-1">{c.contract_number}</Badge>
                                            ))}
                                        </td>
                                        <td className="px-6 py-4">
                                            <Button 
                                                variant="outline" 
                                                size="sm"
                                                onClick={() => { setSelectedPartner(partner); setIsContractModalOpen(true); }}
                                                className="text-xs"
                                            >
                                                <FileText className="w-3 h-3 ml-1" />
                                                إدارة العقود ({partner.contracts?.length || 0})
                                            </Button>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center justify-center gap-1">
                                                <button 
                                                    onClick={() => { setEditingPartner(partner); setIsPartnerModalOpen(true); }}
                                                    className="p-1.5 rounded-md text-slate-400 hover:text-primary hover:bg-primary/5 transition-colors"
                                                    title="تعديل"
                                                >
                                                    <Edit2 size={16} />
                                                </button>
                                                <button 
                                                    onClick={() => handleDeletePartner(partner.id)}
                                                    className="p-1.5 rounded-md text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                                                    title="حذف"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </Card>

            <Modal
                isOpen={isPartnerModalOpen}
                onClose={() => setIsPartnerModalOpen(false)}
                title={editingPartner ? "تعديل شريك" : "إضافة شريك جديد"}
            >
                <PartnerForm 
                    initialData={editingPartner} 
                    onSubmit={handlePartnerSubmit} 
                />
            </Modal>

            <Modal
                isOpen={isContractModalOpen}
                onClose={() => { setIsContractModalOpen(false); setSelectedPartner(null); refresh(); }}
                title={`عقود الشريك: ${currentPartner?.name}`}
                size="2xl"
            >
                {currentPartner && (
                    <div className="space-y-6 text-right">
                        <ContractForm onSubmit={handleContractSubmit} />
                        
                        <div className="mt-6 border-t pt-4">
                            <h3 className="text-sm font-bold text-slate-700 mb-4">العقود المسجلة</h3>
                            <div className="space-y-3">
                                {currentPartner.contracts?.map(contract => (
                                    <div key={contract.id} className="flex items-center justify-between p-3 border rounded-xl bg-slate-50">
                                        <div className="flex items-center gap-3">
                                            <div className={`p-2 rounded-lg ${contract.status === 'active' ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-200 text-slate-500'}`}>
                                                <FileText size={16} />
                                            </div>
                                            <div>
                                                <div className="text-sm font-bold">{contract.contract_number}</div>
                                                <div className="text-[10px] text-slate-500">
                                                    {contract.status === 'active' ? 'نشط' : 'منتهي'}
                                                    {contract.start_date && ` | البداية: ${contract.start_date}`}
                                                    {contract.end_date && ` | الانتهاء: ${contract.end_date}`}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {contract.status === 'active' ? (
                                                <button 
                                                    className="px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors bg-red-50 hover:bg-red-100 text-red-600"
                                                    onClick={() => handleUpdateContractStatus(currentPartner.id, contract.id, 'expired')}
                                                >
                                                    تحويل إلى منتهي
                                                </button>
                                            ) : (
                                                <button 
                                                    className="px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors bg-emerald-50 hover:bg-emerald-100 text-emerald-600"
                                                    onClick={() => handleUpdateContractStatus(currentPartner.id, contract.id, 'active')}
                                                >
                                                    تحويل إلى نشط
                                                </button>
                                            )}
                                            <button 
                                                onClick={() => handleDeleteContract(currentPartner.id, contract.id)}
                                                className="p-1.5 rounded-md text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                                                title="حذف العقد"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                                {(!currentPartner.contracts || currentPartner.contracts.length === 0) && (
                                    <p className="text-center text-sm text-slate-400 py-4">لا توجد عقود مسجلة</p>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

const PartnerForm = ({ onSubmit, initialData }) => {
    const [name, setName] = useState(initialData?.name || '');
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        await onSubmit({ name });
        setSubmitting(false);
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4 text-right">
            <Input 
                label="اسم الشريك" 
                required 
                value={name}
                onChange={e => setName(e.target.value)}
                icon={Building}
            />
            <div className="flex justify-end pt-4 border-t">
                <Button type="submit" isLoading={submitting}>حفظ</Button>
            </div>
        </form>
    );
};

const ContractForm = ({ onSubmit }) => {
    const [contract_number, setContractNumber] = useState('');
    const [start_date, setStartDate] = useState('');
    const [end_date, setEndDate] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        await onSubmit({ contract_number, start_date, end_date });
        setContractNumber('');
        setStartDate('');
        setEndDate('');
        setSubmitting(false);
    };

    return (
        <form onSubmit={handleSubmit} className="flex flex-col gap-3 bg-white p-4 rounded-xl border border-primary/20 shadow-sm">
            <div className="flex items-end gap-3 w-full">
                <div className="flex-1">
                    <Input 
                        label="رقم العقد الجديد" 
                        required 
                        value={contract_number}
                        onChange={e => setContractNumber(e.target.value)}
                        icon={FileText}
                    />
                </div>
                <div className="flex-1">
                    <Input 
                        type="date"
                        label="تاريخ بداية الشراكة" 
                        value={start_date}
                        onChange={e => setStartDate(e.target.value)}
                    />
                </div>
                <div className="flex-1">
                    <Input 
                        type="date"
                        label="تاريخ الانتهاء" 
                        value={end_date}
                        onChange={e => setEndDate(e.target.value)}
                    />
                </div>
            </div>
            <div className="flex justify-end">
                <Button type="submit" isLoading={submitting}>إضافة عقد</Button>
            </div>
        </form>
    );
};

export default PartnerList;
